// e1c/src/pages/StaffRegistration.js
// Author: Sunil Jain

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Container, Paper, Grid, TextField, Button,
    CircularProgress, Alert, Link as MuiLink, RadioGroup, FormControlLabel,
    Radio, FormLabel, FormControl, IconButton, InputAdornment, Avatar, Skeleton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

import logger from '../utils/logger';
import authFetch from '../utils/authFetch';
import { UserRole } from '../utils/enums';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const STAFF_REGISTER_ENDPOINT = '/auth/register-staff';
const NPI_REGEX = /^\d{10}$/;

// API function to verify the token and get invite details
const verifyInviteTokenAPI = async (token) => {
    if (!token) return null;
    // --- START EDIT ---
    // REASON: This aligns the staff flow with the new unified verification endpoint.
    // The backend response now includes the 'role_hint' and 'tenant_name' this component needs.
    return authFetch(`/api/auth/verify-invite?token=${token}`); // <<< MODIFIED
    // --- END EDIT ---
};

function StaffRegistration() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    // This query will run on page load to get the details for the token
    const {
        data: inviteDetails,
        isLoading: isLoadingInvite,
        isError: isInviteError,
        error: inviteErrorDetails,
    } = useQuery({
        queryKey: ['verifyStaffInvite', token],
        queryFn: () => verifyInviteTokenAPI(token),
        enabled: !!token, // Only run the query if a token exists in the URL
        staleTime: Infinity, // These details will not change
        gcTime: Infinity,
        retry: false,
    });

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        npi: '',
        title: '',
        role: '', // Will be pre-filled from inviteDetails
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (isInviteError) {
            setError(inviteErrorDetails?.detail || 'This invitation link is invalid or has expired.');
        } else if (inviteDetails) {
            setFormData(prev => ({
                ...prev,
                email: inviteDetails.email,
                role: inviteDetails.role_hint,
            }));
            setError('');
        }
    }, [inviteDetails, isInviteError, inviteErrorDetails]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (error) setError('');
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
    const handleMouseDownPassword = (event) => { event.preventDefault(); };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        // Validations
        if (!token || !inviteDetails) { setError('Cannot register. The invitation link is invalid.'); return; }
        const requiredFields = ['password', 'confirmPassword', 'first_name', 'last_name', 'npi'];
        const missingFields = requiredFields.filter(field => !formData[field]);
        if (missingFields.length > 0) {
            setError(`Please fill in all required fields: ${missingFields.join(', ')}`); return;
        }
        if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return; }
        if (formData.password.length < 8) { setError('Password must be at least 8 characters long.'); return; }
        if (!NPI_REGEX.test(formData.npi)) { setError('NPI must be exactly 10 digits.'); return; }
        
        setLoading(true);
        const payload = {
            registration_token: token,
            email: formData.email,
            password: formData.password,
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone_number: formData.phone_number.trim() || null,
            role: formData.role, 
            npi: formData.npi.trim(),
            title: formData.title.trim() || null,
        };
        
        logger.debug("Attempting staff registration with payload:", { ...payload, password: '***' });
        
        try {
            const responseData = await authFetch(STAFF_REGISTER_ENDPOINT, { method: 'POST', body: payload });
            
            logger.info("Staff registration successful:", responseData);
            setSuccess(responseData?.message || 'Registration successful! You can now log in.');
        
        } catch (err) {
            logger.error("Staff registration failed:", err);
            const errorMessage = err.message || 'An unexpected error occurred during registration.';
            setError(errorMessage);
        } finally {

            setLoading(false);
        }
    };
    
    return (
        <Container component="main" maxWidth="sm">
            <Box sx={{pt: 4}}/>
            <Paper elevation={3} sx={{ marginTop: 4, padding: { xs: 2, sm: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}><PersonAddAltIcon /></Avatar>
                    <Typography component="h1" variant="h5" gutterBottom> Staff Registration </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        You have been invited to join <strong>{isLoadingInvite ? <Skeleton width="120px" sx={{display: 'inline-block'}}/> : inviteDetails?.tenant_name}</strong>. Please complete the form below to activate your account.
                    </Typography>

                    {(error || isInviteError) && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error || 'This invitation is invalid or has expired.'}</Alert>}
                    {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success} <MuiLink component={RouterLink} to="/login">Proceed to Login</MuiLink></Alert>}

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                             <Grid item xs={12}> <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Account Details</Typography> </Grid>
                             <Grid item xs={12}>
                                 {isLoadingInvite ? <Skeleton variant="rounded" height={56} /> :
                                     <TextField fullWidth required id="email" label="Email Address" name="email" value={formData.email} disabled />
                                 }
                             </Grid>
                             <Grid item xs={12}>
                                <FormControl component="fieldset" required margin="normal" disabled>
                                    <FormLabel component="legend">Assigned Role</FormLabel>
                                    {isLoadingInvite ? <Skeleton variant="rounded" height={42} /> : 
                                        <RadioGroup row name="role" value={formData.role}>
                                            {/* Only the single, locked role is shown */}
                                            {formData.role === UserRole.TENANT_ADMIN && <FormControlLabel value={UserRole.TENANT_ADMIN} control={<Radio checked />} label="Tenant Administrator" />}
                                            {formData.role === UserRole.PHYSICIAN_QHP && <FormControlLabel value={UserRole.PHYSICIAN_QHP} control={<Radio checked />} label="Physician / QHP" />}
                                            {formData.role === UserRole.CLINICAL_STAFF && <FormControlLabel value={UserRole.CLINICAL_STAFF} control={<Radio checked />} label="Clinical Staff (Non-QHP)" />}
                                        </RadioGroup>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" required fullWidth name="password" label="Create Password" type={showPassword ? 'text' : 'password'} id="password" value={formData.password} onChange={handleChange} disabled={loading || !!success} helperText="Minimum 8 characters" InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}> {showPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" required fullWidth name="confirmPassword" label="Confirm Password" type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleChange} disabled={loading || !!success} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton onClick={handleClickShowConfirmPassword} onMouseDown={handleMouseDownPassword}> {showConfirmPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} /> </Grid>
                            
                            <Grid item xs={12}> <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>Your Information</Typography> </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" required fullWidth id="first_name" label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} disabled={loading || !!success} /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" required fullWidth id="last_name" label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} disabled={loading || !!success} /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" required fullWidth id="npi" label="NPI Number" name="npi" value={formData.npi} onChange={handleChange} disabled={loading || !!success} helperText="10-digit individual NPI" inputProps={{ maxLength: 10 }} /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField margin="dense" fullWidth id="title" label="Professional Title (e.g., MD, PA, RN)" name="title" value={formData.title} onChange={handleChange} disabled={loading || !!success} /> </Grid>
                             <Grid item xs={12} sm={6}> <TextField margin="dense" fullWidth id="phone_number" label="Phone Number" name="phone_number" value={formData.phone_number} onChange={handleChange} disabled={loading || !!success} /> </Grid>
                        </Grid>
                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading || !!success || !inviteDetails} >
                            {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
                        </Button>
                        <Grid container justifyContent="flex-end">
                            <Grid item> <MuiLink component={RouterLink} to="/login" variant="body2"> Already have an account? Sign In </MuiLink> </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Paper>
            <Box sx={{pb: 4}}/>
        </Container>
    );
}

export default StaffRegistration;